import {DatePicker} from 'antd';
import moment from 'moment';
import styles from './dashboard.module.scss';

/**
 * Month selection for the growth charts.
 *
 * Three things this replaces.
 *
 * There were two pickers, Month and Year, but choosing a month already set the
 * year (`form.setFieldsValue({month: date, year: date})`), so the Year field
 * was a second control for something already decided — and its own handler
 * carried a different set of bounds. One month picker says it once.
 *
 * Stepping to an adjacent month meant *scrolling the chart to its left edge*,
 * which then silently replaced the data and rewrote both pickers. Nothing on
 * screen suggested that, and the same gesture on a trackpad happens by
 * accident. Previous/Next buttons say what they do.
 *
 * The bounds also disagreed with themselves: the picker disabled anything
 * before July 2022 while the toast said July 2021. July 2021 is the real floor.
 */

const FLOOR = moment('2021-07-01');

const Chevron = ({direction}) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

export const MonthNavigator = ({value, onChange, disabled}) => {
  const current = value ?? moment();
  const atFloor = current.isSameOrBefore(FLOOR, 'month');
  const atCeiling = current.isSameOrAfter(moment(), 'month');

  const step = amount => {
    const next = current.clone().add(amount, 'month');
    if (next.isBefore(FLOOR, 'month') || next.isAfter(moment(), 'month'))
      return;
    onChange(next);
  };

  return (
    <div className={styles.monthNav}>
      <button
        type="button"
        className={styles.railArrow}
        aria-label="Previous month"
        disabled={disabled || atFloor}
        onClick={() => step(-1)}>
        <Chevron direction="left" />
      </button>

      <DatePicker.MonthPicker
        className={styles.monthPicker}
        value={current}
        allowClear={false}
        disabled={disabled}
        format="MMMM YYYY"
        onChange={date => date && onChange(date)}
        disabledDate={date =>
          date.isAfter(moment(), 'month') || date.isBefore(FLOOR, 'month')
        }
      />

      <button
        type="button"
        className={styles.railArrow}
        aria-label="Next month"
        disabled={disabled || atCeiling}
        onClick={() => step(1)}>
        <Chevron direction="right" />
      </button>
    </div>
  );
};

export default MonthNavigator;
